import React from 'react';
import { Box, Card, Typography, Chip, Grid, Stack, CardMedia, Divider } from '@mui/material';
import { IAudition, IRole } from '../../types/global/helper';
import { TextWithIcon } from '../TextWithIcon/TextWithIcon';
import MaskIcon from '../CustomSVGIcons/MaskIcon';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { convertMinuteSecondsDays, formatNumberWithK, formatWithOrdinal } from '../../utils/AppUtils';
import CustomButton from '../Button/CustomButton';
import { CustomChipGroup } from '../Chip/CustomChipGroup';
import { useAuth } from '../../hook/useAuth';
import { RichTextViewer } from '../Common/RichTextViewer';
import { CompanyAvatar } from '../Avatar/CompanyAvatar';
import { StarRating } from '../EsVideoVotingModal/EsRatingStars';

interface IAlternateAuditionCard {
    audition: IAudition;
    onViewAndApplyClick: () => void;
    onApplyClicked: (role: IRole, audition?: IAudition) => void;
}

export const AlternateAuditionCard: React.FC<IAlternateAuditionCard> = ({
    audition,
    onApplyClicked,
    onViewAndApplyClick,
}) => {
    const { themeStyle } = useThemeContext();
    const { user_id } = useAuth();

    const hasRoles =
        audition.roles && audition.roles.length > 0 && audition.roles.filter(role => role !== null).length > 0;

    const isExternalUser = user_id !== audition.created_by._id;

    const RoleRender = ({ role }: { role: IRole }) => {
        return (
            <Box minWidth={'150px'} mb={1}>
                <Box textAlign="left" p={1} borderRadius={1} bgcolor={theme[themeStyle].secondaryBg}>
                    <TextWithIcon
                        text={role.character}
                        typographyProps={{
                            sx: {
                                fontSize: 16,
                                textTransform: 'capitalize',
                            },
                        }}
                        icon={<MaskIcon height={'15px'} width={'15px'} stroke={theme[themeStyle].tag_white} />}
                    />
                    <Typography
                        color={theme[themeStyle].tag_white}
                        textTransform={'capitalize'}
                        fontFamily={FONT_ENUM.MAIN_MEDIUM}
                        fontSize={14}>
                        {role.role_type === 'cast' &&
                            role.gender &&
                            `${role.gender},  ${role.min_age} - ${role.max_age}`}
                    </Typography>
                    {isExternalUser && (
                        <CustomButton
                            label="Apply"
                            customStyle={{ borderRadius: 4 }}
                            sx={{
                                mt: 1,
                                '&:hover': {
                                    WebkitTextFillColor: theme[themeStyle].primary_orange,
                                },
                            }}
                            onClick={() => onApplyClicked(role, audition)}
                            withAuthentication
                        />
                    )}
                </Box>
            </Box>
        );
    };
    return (
        <Card
            sx={{
                maxWidth: 800,
                borderRadius: 2,
                boxShadow: 3,
                border: `0.5px solid ${theme[themeStyle].tag_white}`,
                backgroundColor: theme[themeStyle].secondaryBg,
            }}>
            <Box sx={{ borderTop: `4px solid ${audition.is_feature ? '#f88b78' : theme[themeStyle].highlight}` }} />
            <Box sx={{ p: 0, pb: 0 }}>
                <Stack direction="row">
                    <Stack
                        borderRight={hasRoles ? 1 : 0}
                        p={1}
                        width={'100%'}
                        borderColor={theme[themeStyle].darkGray60}>
                        {/* Chips */}
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                            <Box display="flex" gap={1}>
                                {audition.is_feature && (
                                    <Chip
                                        label="FEATURED"
                                        sx={{
                                            backgroundColor: `#f88b78`,
                                            color: 'white',
                                            fontWeight: 'bold',
                                            fontSize: 10,
                                        }}
                                    />
                                )}
                                {audition.compensation && audition.compensation === 'paid' && (
                                    <Chip
                                        label="PAID"
                                        sx={{
                                            backgroundColor: theme[themeStyle].switchGreen,
                                            color: 'white',
                                            fontWeight: 'bold',
                                            fontSize: 10,
                                        }}
                                    />
                                )}

                                <CustomChipGroup
                                    tags={audition?.tags?.filter(t => t.toLowerCase() !== 'paid')}
                                    maxVisible={2}
                                    tagSx={{
                                        backgroundColor: 'black',
                                        color: 'white',
                                        textTransform: 'uppercase',
                                        height: '32px',
                                        borderRadius: '40px',
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                    }}
                                />
                            </Box>
                            <Box>
                                <Typography
                                    style={{
                                        fontSize: 12,
                                        color: theme[themeStyle].tag_white,
                                        opacity: 0.5,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        fontStyle: 'italic',
                                    }}>
                                    Posted {convertMinuteSecondsDays(audition.createdAt)} ago
                                </Typography>
                            </Box>
                        </Stack>

                        {audition.imageCover && (
                            <Box sx={{ position: 'relative' }}>
                                <CardMedia
                                    component="img"
                                    image={audition.imageCover}
                                    alt={`${audition.title}_image`}
                                    sx={{
                                        objectFit: 'cover',
                                        height: '250px',
                                        width: '100%',
                                    }}
                                />
                                {/* Union affiliation and type of work chips */}
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    sx={{
                                        position: 'absolute',
                                        top: 5,
                                        left: 5,
                                        zIndex: 1,
                                    }}>
                                    {audition.union_affiliation && (
                                        <Chip
                                            label={audition.union_affiliation}
                                            size="small"
                                            sx={{
                                                backgroundColor: theme[themeStyle].primary_orange,
                                                color: theme[themeStyle].true_white,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                fontSize: 8,
                                                height: '20px',
                                            }}
                                        />
                                    )}
                                    {audition.type_of_work && (
                                        <Chip
                                            label={audition.type_of_work}
                                            size="small"
                                            sx={{
                                                backgroundColor: theme[themeStyle].highlight,
                                                color: theme[themeStyle].true_white,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                fontSize: 8,
                                                height: '20px',
                                            }}
                                        />
                                    )}
                                </Stack>
                            </Box>
                        )}
                        <Stack p={1}>
                            {/* Title */}
                            <Typography
                                variant="h5"
                                component="h2"
                                fontWeight="bold"
                                gutterBottom
                                color={theme[themeStyle].tag_white}>
                                {audition.title}
                            </Typography>
                            <Typography color={theme[themeStyle].tag_white} textTransform={'capitalize'}>
                                {audition.talent_location?.address}
                            </Typography>

                            {/* Description */}
                            <Typography variant="subtitle1" mt={1} mb={1} color={theme[themeStyle].tag_white}>
                                <strong>Description:</strong>
                            </Typography>
                            <RichTextViewer
                                content={audition.synopsis}
                                textStyle={{
                                    color: theme[themeStyle].tag_white,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    padding: 0,
                                }}
                            />

                            {/* Usage */}
                            {audition.usage && audition.usage.length > 0 && (
                                <Box mb={2}>
                                    <Typography variant="subtitle1" color={theme[themeStyle].tag_white} mb={1}>
                                        <strong>Usage:</strong>
                                    </Typography>
                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                        {audition.usage.map((usageItem, index) => (
                                            <Chip
                                                key={index}
                                                label={usageItem}
                                                size="small"
                                                sx={{
                                                    backgroundColor: theme[themeStyle].secondaryBg,
                                                    color: theme[themeStyle].tag_white,
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    fontSize: 10,
                                                    height: '24px',
                                                }}
                                            />
                                        ))}
                                    </Stack>
                                </Box>
                            )}

                            {/* Dates and Locations */}
                            <Typography variant="subtitle1" color={theme[themeStyle].tag_white} mb={1}>
                                <strong>Dates & Locations:</strong>
                            </Typography>
                            <Typography
                                variant="body1"
                                color={theme[themeStyle].tag_white}
                                mb={3}
                                fontFamily={FONT_ENUM.MAIN_MEDIUM}>
                                {formatWithOrdinal(audition.job_start_date)}.{' '}
                                {audition?.shoot_location?.address ? audition?.shoot_location?.address : 'Remote'}.
                            </Typography>

                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                sx={{ my: 2, pr: 1 }}>
                                {/* Avatar and Info */}
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <CompanyAvatar
                                        company={audition?.production_company!}
                                        enableDefaultNavigation
                                        size="large"
                                    />
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                fontSize: 10,
                                                color: theme[themeStyle].tag_white,
                                            }}>
                                            {audition?.production_company.company_name}
                                        </Typography>
                                        <Stack direction="row" alignItems="center" spacing={0.5}>
                                            <StarRating
                                                disabled
                                                style={{
                                                    paddingLeft: 0,
                                                    paddingRight: 0,
                                                    height: '10px',
                                                    width: 'fit-content',
                                                }}
                                                ratingProps={{
                                                    value: audition?.production_company.rating,
                                                    readOnly: true,
                                                    sx: {
                                                        fontSize: 16,
                                                        color: theme[themeStyle].highlight,
                                                    },
                                                }}
                                            />
                                            <Typography
                                                sx={{
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    fontSize: 10,
                                                    color: theme[themeStyle].tag_white,
                                                }}>
                                                {formatNumberWithK(audition?.production_company.number_of_reviews!)}
                                            </Typography>
                                        </Stack>
                                    </Box>
                                </Stack>

                                <Box>
                                    <Stack direction="row" alignItems="center" spacing={0.5} px={2}>
                                        <WorkOutlineIcon
                                            sx={{ fontSize: '1rem', color: theme[themeStyle].highlight }}
                                        />
                                        <Typography
                                            sx={{
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                fontSize: 10,
                                                color: theme[themeStyle].highlight,
                                            }}>
                                            {audition?.production_company.number_of_jobs} Job
                                            {audition &&
                                            audition?.production_company &&
                                            audition?.production_company.number_of_jobs &&
                                            audition?.production_company.number_of_jobs > 1
                                                ? 's'
                                                : ''}
                                        </Typography>
                                    </Stack>
                                </Box>
                            </Stack>

                            <Divider sx={{ mb: 2 }} />

                            {/* Apply Section */}
                            <Grid container justifyContent="space-between" alignItems="center">
                                <Grid item>
                                    <CustomButton
                                        label={isExternalUser ? 'View Details & Apply' : 'View Details'}
                                        customStyle={{ borderRadius: 4, minWidth: '200px' }}
                                        onClick={onViewAndApplyClick}
                                        sx={{
                                            '&:hover': {
                                                WebkitTextFillColor: theme[themeStyle].primary_orange,
                                            },
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </Stack>
                    </Stack>
                    {hasRoles && (
                        <Stack p={1} sx={{ backgroundColor: theme[themeStyle].deep_grey }}>
                            {audition.roles &&
                                audition.roles.map((role, index) => {
                                    if (role) {
                                        return <RoleRender key={index} role={role} />;
                                    }
                                    return <div />;
                                })}
                        </Stack>
                    )}
                </Stack>
            </Box>
        </Card>
    );
};
